import { api } from './api';

export interface HSNCode {
  id: string;
  code: string;
  description: string;
  chapter?: string | null;
  heading?: string | null;
  // Total GST rate; CGST and SGST are half of it each and IGST all of it
  gstRate: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cessRate: number;
  unit?: string | null;
  isActive: boolean;
  notes?: string | null;
  // Invoice lines that carry this code; filled in on the paged list
  invoiceLines?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHSNCodeDto {
  code: string;
  description: string;
  gstRate?: number;
  cessRate?: number;
  // Null clears an optional text on an update
  unit?: string | null;
  isActive?: boolean;
  notes?: string | null;
}

export type UpdateHSNCodeDto = Partial<CreateHSNCodeDto>;

export type HSNSortField = 'code' | 'description' | 'gstRate' | 'createdAt';

export interface HSNCodeQueryParams {
  page: number;
  limit: number;
  search?: string;
  isActive?: boolean;
  gstRate?: number;
  sortBy?: HSNSortField;
  sortOrder?: 'ASC' | 'DESC';
}

export interface PaginatedHSNCodes {
  data: HSNCode[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface HSNCodeSummary {
  total: number;
  active: number;
  inactive: number;
  // How many codes are taxed at each GST rate, lowest rate first
  rates: { gstRate: number; codes: number }[];
}

export class HSNCodeService {
  // Active codes only: the list invoices choose from
  static async getAllHSNCodes(): Promise<HSNCode[]> {
    const response = await api.get('/gst/hsn-codes');
    return response.data;
  }

  // One page of codes, inactive ones included
  static async getHSNCodesPage(query: HSNCodeQueryParams): Promise<PaginatedHSNCodes> {
    const params = {
      page: query.page,
      limit: query.limit,
      ...(query.search ? { search: query.search } : {}),
      ...(query.isActive !== undefined ? { isActive: query.isActive } : {}),
      ...(query.gstRate !== undefined ? { gstRate: query.gstRate } : {}),
      ...(query.sortBy ? { sortBy: query.sortBy } : {}),
      ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
    };
    const response = await api.get<PaginatedHSNCodes>('/gst/hsn-codes/paginated', { params });
    return response.data;
  }

  static async getHSNCodeSummary(): Promise<HSNCodeSummary> {
    const response = await api.get<HSNCodeSummary>('/gst/hsn-codes/summary');
    return response.data;
  }

  static async getHSNCodeById(id: string): Promise<HSNCode> {
    const response = await api.get(`/gst/hsn-codes/${id}`);
    return response.data;
  }

  static async createHSNCode(data: CreateHSNCodeDto): Promise<HSNCode> {
    const response = await api.post('/gst/hsn-codes', data);
    return response.data;
  }

  static async updateHSNCode(id: string, data: UpdateHSNCodeDto): Promise<HSNCode> {
    const response = await api.patch(`/gst/hsn-codes/${id}`, data);
    return response.data;
  }

  static async deleteHSNCode(id: string): Promise<void> {
    await api.delete(`/gst/hsn-codes/${id}`);
  }
}
